import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getLocalizedPathForLang } from '@/config/routes';


export type Language = 'el' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  getLocalizedPath: (path: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translation keys organized by section
const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navbar - Keep in English as requested
    'nav.home': 'Home',
    'nav.analysis': 'Audit',
    'nav.benefits': 'Benefits',
    'nav.pricing': 'Pricing',
    'nav.agencies': 'For Agencies',
    'nav.contact': 'Contact',
    'nav.login': 'Login',
    'nav.dashboard': 'Dashboard',
    'nav.blog': 'Blog',
    'nav.faq': 'FAQ',
    'nav.freeCro': 'Free CRO Audit',
    'nav.rewards': 'Rewards',
    'megamenu.rewardsDesc': 'Earn free credits with referrals & sharing',
    
    // MegaMenu - Keep in English
    'megamenu.products': 'Products',
    'megamenu.solutions': 'Solutions',
    'megamenu.analysisTypes': 'Audit Types',
    'megamenu.features': 'Features',
    'megamenu.learn': 'Learn',
    'megamenu.resources': 'Resources',
    'megamenu.ecommerce': 'E-commerce CRO Audit',
    'megamenu.ecommerceDesc': 'Product, category, cart and checkout pages',
    'megamenu.leadgen': 'Lead Generation CRO Audit',
    'megamenu.leadgenDesc': 'The whole landing page, from headline to form',
    'megamenu.platform': 'Platform',
    'megamenu.whatIsEyeCaptain': 'What is EyeCaptain',
    'megamenu.whatIsEyeCaptainDesc': 'What it audits, for whom, and what you get',
    'megamenu.croAuditGuide': 'CRO Audit Guide',
    'megamenu.croAuditGuideDesc': 'What to check and how to run one',
    'megamenu.croChecklist': 'CRO Checklist',
    'megamenu.croChecklistDesc': 'The points every converting page gets right',
    'megamenu.contactDesc': 'Demos, plans and help with a report',
    'megamenu.croPageAudit': 'CRO Page Audit',
    'megamenu.croPageAuditDesc': 'One audit, five kinds of site',
    'megamenu.croOverview': 'All site types',
    'megamenu.croOverviewDesc': 'How the audit adapts to your site',
    'megamenu.siteTypes': 'Site types',
    'megamenu.saas': 'SaaS',
    'megamenu.saasDesc': 'Pricing, sign-up and trial pages',
    'megamenu.booking': 'Booking',
    'megamenu.bookingDesc': 'Hotels, clinics, restaurants and tours',
    'megamenu.information': 'Information sites',
    'megamenu.informationDesc': 'Blogs, publishers and newsletters',
    'megamenu.beyondOnePage': 'Beyond one page',
    'megamenu.funnel': 'Funnel Audit',
    'megamenu.funnelDesc': 'Walk the whole path, see where people drop',
    'megamenu.userAgent': 'User Agent',
    'megamenu.userAgentDesc': 'An AI visitor tries to buy, book or sign up',
    'megamenu.dataAndAi': 'Data and AI',
    'megamenu.analyticsIntegrations': 'Analytics integrations',
    'megamenu.analyticsIntegrationsDesc': 'Your GA4, Clarity and more inside the audit',
    'megamenu.visualMockups': 'Visual mockups',
    'megamenu.visualMockupsDesc': 'See a suggestion on your own page',
    'megamenu.aiAgent': 'AI Agent',
    'megamenu.aiAgentDesc': 'Ask about your site, audits and analytics',
    'megamenu.liveHeatmaps': 'Live heatmaps',
    'megamenu.liveHeatmapsDesc': 'Real visitors, explained by AI',
    'megamenu.abTestingFeature': 'A/B testing',
    'megamenu.abTestingFeatureDesc': 'Test the fixes from your audit',
    'megamenu.figmaDesign': 'Figma design audit',
    'megamenu.figmaDesignDesc': 'Review a design before it is built',
    'megamenu.otherAudits': 'More audits',
    'megamenu.sitesFeaturedTitle': 'Every report reads your site type',
    'megamenu.sitesFeaturedDesc': 'A store is judged on its buy box, a SaaS page on its plans, a clinic on its booking box.',
    'megamenu.sitesFeaturedCta': 'See the CRO Page Audit',
    'megamenu.custom': 'Custom Audit',
    'megamenu.customDesc': 'Tailored CRO for your specific needs',
    'megamenu.agenciesFeaturedTitle': 'For Agencies',
    'megamenu.agenciesFeaturedDesc': 'You don\'t need a CRO Specialist when you have a CRO Agent',
    'megamenu.agenciesFeaturedCta': 'Learn More',
    'megamenu.competitorComparison': 'Competition Comparison',
    'megamenu.competitorComparisonDesc': 'Compare your UX against competitors',
    'megamenu.contentAnalysis': 'Content Audit',
    'megamenu.contentAnalysisDesc': 'Optimize your content for conversions',
    'megamenu.technicalAnalysis': 'Technical Audit',
    'megamenu.technicalAnalysisDesc': 'Performance, security & accessibility audit',
    'megamenu.figmaAnalysis': 'Figma Audit',
    'megamenu.figmaAnalysisDesc': 'AI design audit for mockups',
    'megamenu.aiHeatmaps': 'AI Predictive Attention',
    'megamenu.aiHeatmapsDesc': 'Predictive eye-tracking audit',
    'megamenu.neuromarketing': 'Neuromarketing',
    'megamenu.neuromarketingDesc': 'Psychology-based optimization',
    'megamenu.croTechniques': 'CRO Techniques',
    'megamenu.croTechniquesDesc': 'Conversion rate optimization strategies',
    'megamenu.aiCaptain': 'AI Captain',
    'megamenu.aiCaptainDesc': 'Interactive AI insights on your audit',
    'megamenu.googleAnalytics': 'Google Analytics Integration',
    'megamenu.googleAnalyticsDesc': 'Connect GA4 data to your CRO audit',
    'megamenu.pagespeedInsights': 'PageSpeed Insights',
    'megamenu.pagespeedInsightsDesc': 'Core Web Vitals & performance metrics',
    'megamenu.whatIsUx': 'What is UX?',
    'megamenu.whatIsUxDesc': 'Learn about User Experience design',
    'megamenu.whatIsCro': 'What is CRO?',
    'megamenu.whatIsCroDesc': 'Conversion Rate Optimization explained',
    'megamenu.howItWorks': 'How It Works',
    'megamenu.howItWorksDesc': 'Our AI-powered audit process',
    'megamenu.blogDesc': 'Articles & insights on CRO',
    'megamenu.benefitsDesc': 'Why choose EyeCaptain',
    'megamenu.faq': 'FAQ',
    'megamenu.faqDesc': 'Frequently asked questions',
    'megamenu.agenciesDesc': 'CRO services for your clients',
    'megamenu.freeCroDesc': 'Get a free CRO audit of your site',
    'megamenu.freeWebsiteAudit': 'Free CRO Audit',
    'megamenu.freeWebsiteAuditDesc': 'AI-powered UX audit with heatmaps & insights',
    'megamenu.freeAudit': 'Free Audit',
    'nav.freeSignup': 'Free signup',
    'nav.freeSignupHint': 'No card · 30 free credits',
    'megamenu.pricingOptions': 'Pricing Options',
    'megamenu.subscription': 'Subscription Plans',
    'megamenu.subscriptionDesc': 'Monthly credits with recurring billing',
    'megamenu.payAsYouGo': 'Pay As You Go',
    'megamenu.payAsYouGoDesc': 'Lifetime credits without commitments',
    'megamenu.croHub': 'CRO Hub',
    'megamenu.croHubLearn': 'Learn CRO',
    'megamenu.croHubTools': 'Blog & Help',
    'megamenu.abTesting': 'A/B Testing Guide',
    'megamenu.abTestingDesc': 'Master A/B testing methodology',
    'megamenu.heatmapGuide': 'Heatmap Audit',
    'megamenu.heatmapGuideDesc': 'Understand user behavior visually',
    'megamenu.ecommerceCro': 'E-commerce CRO',
    'megamenu.ecommerceCroDesc': 'Optimize your online store conversions',
    'megamenu.aiCro': 'AI in CRO',
    'megamenu.aiCroDesc': 'How AI transforms conversion optimization',
    'megamenu.uxAuditGuide': 'UX Audit Guide',
    'megamenu.uxAuditGuideDesc': 'Step-by-step UX auditing process',
    'megamenu.croTools': 'CRO Tools',
    'megamenu.croToolsDesc': 'Best tools for conversion optimization',
    'megamenu.aiHub': 'AI Hub',
    'megamenu.aiHubGuides': 'AI Guides',
    'megamenu.aiHubFeatures': 'AI Features',
    'megamenu.aiToolsGuide': 'Best AI Tools 2026',
    'megamenu.aiToolsGuideDesc': 'ChatGPT, Gemini, Claude & more',
    'megamenu.aiModelsGuide': 'Top AI Models 2026',
    'megamenu.aiModelsGuideDesc': 'GPT-4o vs Gemini vs Claude comparison',
    'megamenu.aiMarketingGuide': 'AI Marketing Guide',
    'megamenu.aiMarketingGuideDesc': 'AI strategies for marketing',
    'megamenu.aiPromptGuide': 'AI Prompting Guide',
    'megamenu.aiPromptGuideDesc': 'Master the art of AI prompting',
    'megamenu.croToolsGuide': 'Best CRO Tools 2026',
    'megamenu.croToolsGuideDesc': 'Top 100 CRO tools ranked & reviewed',
    'megamenu.analyticsHub': 'Analytics Hub',
    'megamenu.analyticsHubTopics': 'Analytics Topics',
    'megamenu.analyticsHubTools': 'Tools & Reporting',
    'megamenu.analyticsHubOverview': 'Analytics Hub',
    'megamenu.analyticsHubOverviewDesc': 'Your complete analytics resource center',
    'megamenu.ga4Guide': 'Google Analytics 4',
    'megamenu.ga4GuideDesc': 'Master GA4 setup & reporting',
    'megamenu.cookiePrivacy': 'Cookie & Privacy',
    'megamenu.cookiePrivacyDesc': 'GDPR compliance & cookie tracking',
    'megamenu.gtmGuide': 'Google Tag Manager',
    'megamenu.gtmGuideDesc': 'Tags, triggers & variables',
    'megamenu.reportingGuide': 'Reporting & Dashboards',
    'megamenu.reportingGuideDesc': 'Looker Studio & KPI frameworks',
    'megamenu.resourcesHub': 'Resources',
    'megamenu.croBannerTitle': 'Free CRO Audit',
    'megamenu.croBannerDesc': 'See what your page is losing, on desktop and mobile. Free, no card.',
    'megamenu.croBannerCta': 'Start Free Audit',
    'megamenu.croEbookTitle': 'E-Commerce Website Guide',
    'megamenu.croEbookDesc': 'Free page-by-page guide to build a high-converting online store. 60+ best practices.',
    'megamenu.croEbookCta': 'Download Free Guide',
    'megamenu.aiBannerTitle': 'Explore AI Hub',
    'megamenu.aiBannerDesc': 'Discover how AI is transforming marketing, development, and business strategy.',
    'megamenu.aiBannerCta': 'Explore Guides',
    'megamenu.analyticsBannerTitle': 'Analytics Hub',
    'megamenu.analyticsBannerDesc': 'Master GA4, GTM, and data-driven marketing with our comprehensive guides.',
    'megamenu.analyticsBannerCta': 'Browse Topics',
    
    'hero.badge': 'AI-Powered CRO Platform',
    'hero.title1': 'AI CRO Audit',
    'hero.title2': 'That Tells You',
    'hero.title3': 'What to Change',
    'hero.subtitle': 'Paste a URL and get a conversion rate optimization audit in under five minutes: an attention heatmap, every friction point on desktop and mobile, and the fixes in priority order. No install, no traffic needed, no card.',
    'hero.cta': 'Start Free Audit',
    'hero.demo': 'Watch Demo',
    'hero.noCreditCard': 'No credit card required',
    'hero.resultsIn5Min': 'Results in under 5 minutes',
    'hero.aiPowered': 'AI-Powered Audit',
    'hero.scroll': 'Explore',
    'hero.stat1': '<3 min',
    'hero.stat1label': 'Per audit',
    'hero.stat2': '2',
    'hero.stat2label': 'Devices per report',
    'hero.stat3': '40+',
    'hero.stat3label': 'Neuromarketing triggers',
    
    // Analysis Types
    'analysis.ecommerce.title': 'E-commerce',
    'analysis.ecommerce.desc': 'Checkout optimization, reduce cart abandonment, increase AOV',
    'analysis.ecommerce.stat': '+45% Conversion Rate',
    'analysis.leadgen.title': 'Lead Generation',
    'analysis.leadgen.desc': 'Convert visitors to leads with optimized forms',
    'analysis.leadgen.stat': '+60% Lead Capture',
    'analysis.custom.title': 'Custom Audit',
    'analysis.custom.desc': 'Personalized audit for your specific needs',
    'analysis.custom.stat': 'Tailored Results',
    
    // Problem Section
    'problem.badge': 'The Problem',
    'problem.title': 'Why Are You',
    'problem.titleHighlight': 'Losing Customers?',
    'problem.subtitle': '97% of visitors leave without buying. The problem isn\'t traffic - it\'s the user experience that drives them away.',
    'problem.pain1.title': 'Poor CTA Placement',
    'problem.pain1.problem': 'Buttons aren\'t visible',
    'problem.pain1.loss': '-35% Conversions',
    'problem.pain2.title': 'Cognitive Overload',
    'problem.pain2.problem': 'Too much information, confused users',
    'problem.pain2.loss': '-42% Engagement',
    'problem.pain3.title': 'Slow Checkout',
    'problem.pain3.problem': 'Complex purchase process',
    'problem.pain3.loss': '-67% Cart Recovery',
    
    // Solution Section
    'solution.badge': 'The Solution: EyeCaptain',
    'solution.title1': 'AI-Powered',
    'solution.title2': 'CRO Audit',
    'solution.subtitle': 'We analyze every page on 5 levels and give you specific instructions to convert visitors into customers.',
    'solution.heatmapPreview': 'AI Predictive Attention Preview',
    'solution.clarityScore': 'Clarity Score',
    'solution.ctaVisibility': 'CTA Visibility',
    'solution.timeToCta': 'Time to CTA',
    'solution.resultsTitle': 'What Results You\'ll See',
    'solution.result1.title': '+45% Conversion Rate',
    'solution.result1.desc': 'Average increase after implementing our recommendations',
    'solution.result2.title': '-60% Cart Abandonment',
    'solution.result2.desc': 'Reduce cart abandonment with checkout optimization',
    'solution.result3.title': '12x ROI',
    'solution.result3.desc': 'Every €1 invested returns €12 within the first month',
    
    // Analysis Sections
    'sections.aiHeatmaps': 'AI Predictive Attention',
    'sections.aiHeatmapsDesc': 'Predictive eye-tracking',
    'sections.neuromarketing': 'Neuromarketing',
    'sections.neuromarketingDesc': 'Psychology insights',
    'sections.uxAnalysis': 'UX Audit',
    'sections.uxAnalysisDesc': 'Usability optimization',
    'sections.croTechniques': 'CRO Techniques',
    'sections.croTechniquesDesc': 'Conversion tactics',
    'sections.funnelAnalysis': 'Funnel Audit',
    'sections.funnelAnalysisDesc': 'Drop-off prevention',
    
    // Stats
    'stats.conversionBoost': 'Avg. Conversion Boost',
    'stats.projectsAnalyzed': 'Projects Analyzed',
    'stats.clientSatisfaction': 'Client Satisfaction',
    'stats.analysisTime': 'Audit Time',
    
    // Case Studies
    'cases.title': 'Real Results',
    'cases.subtitle': 'See how our clients increased their conversions with EyeCaptain',
    'cases.before': 'Before',
    'cases.after': 'After',
    'cases.improvement': 'Improvement',
    
    // Free Audit Section
    'freeAudit.badge': 'Free CRO Audit',
    'freeAudit.title': 'Start with a',
    'freeAudit.titleHighlight': 'Free CRO Audit',
    'freeAudit.subtitle': 'Get a complete audit of your page with AI Predictive Attention, neuromarketing insights and actionable recommendations.',
    
    // How It Works (legacy keys - kept for compatibility)
    'howItWorks.title': 'How It Works',
    
    // Footer
    'footer.description': 'AI-Powered CRO Platform for e-commerce and lead generation websites.',
    'footer.quickLinks': 'Quick Links',
    'footer.solutions': 'Solutions',
    'footer.resources': 'Resources',
    'footer.legal': 'Legal',
    'footer.contact': 'Contact',
    'footer.rights': 'All rights reserved.',
    
    // Features
    'features.aiHeatmaps.title': 'AI Predictive Attention',
    'features.aiHeatmaps.desc': 'Predictive eye-tracking without real users',
    'features.neuromarketing.title': 'Neuromarketing Audit',
    'features.neuromarketing.desc': 'Persuasion psychology and emotional triggers',
    'features.cro.title': 'CRO Techniques',
    'features.cro.desc': '47+ proven conversion optimization techniques',
    
    // Common - CTAs always in English
    'common.getStarted': 'Get Started',
    'common.learnMore': 'Learn More',
    'common.tryFree': 'Try Free',
    'common.viewDemo': 'Watch Demo',
    'common.contact': 'Contact',
    'common.seeMore': 'See More',
    'common.readMore': 'Read More',
    'common.close': 'Close',
    'common.submit': 'Submit',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.startNow': 'Start Now',
    'common.before': 'Before',
    'common.after': 'After',
    
    // Benefits Page
    'benefits.badge': 'Why EyeCaptain',
    'benefits.title': 'Why Choose',
    'benefits.titleHighlight': 'EyeCaptain',
    'benefits.subtitle': 'Transform visitors into customers with data-driven CRO optimization',
    'benefits.mainBenefit1.title': 'Increase Conversion Rate',
    'benefits.mainBenefit1.stat': '+45%',
    'benefits.mainBenefit1.statLabel': 'Average increase',
    'benefits.mainBenefit1.desc': 'Average conversion increase after implementing our recommendations',
    'benefits.mainBenefit1.details': 'Our clients see significant improvement within the first month.',
    'benefits.mainBenefit2.title': 'Save Time',
    'benefits.mainBenefit2.stat': '85%',
    'benefits.mainBenefit2.statLabel': 'Less time',
    'benefits.mainBenefit2.desc': 'Automated audit instead of weeks of manual work',
    'benefits.mainBenefit2.details': 'Results in 5 minutes instead of 2-3 weeks.',
    'benefits.mainBenefit3.title': 'ROI Guarantee',
    'benefits.mainBenefit3.stat': '12x',
    'benefits.mainBenefit3.statLabel': 'Return on Investment',
    'benefits.mainBenefit3.desc': 'Every euro invested returns multiple times over',
    'benefits.mainBenefit3.details': 'Data-driven optimization with measurable results.',
    'benefits.resultsTitle': 'Client Results',
    'benefits.resultsSubtitle': 'See how EyeCaptain increased conversions',
    'benefits.additionalTitle': 'Complete Benefits List',
    'benefits.additional1.title': 'AI-Powered Insights',
    'benefits.additional1.desc': 'The most advanced AI models for accurate audit',
    'benefits.additional2.title': 'Instant Results',
    'benefits.additional2.desc': 'Report in minutes, not weeks',
    'benefits.additional3.title': 'Actionable Recommendations',
    'benefits.additional3.desc': 'Specific implementation guidelines',
    'benefits.additional4.title': 'Industry Best Practices',
    'benefits.additional4.desc': 'Based on data from thousands of sites',
    'benefits.additional5.title': 'Priority Scoring',
    'benefits.additional5.desc': 'Scored based on impact and effort',
    'benefits.additional6.title': 'Expert Support',
    'benefits.additional6.desc': 'Support from CRO specialists',
    'benefits.testimonialsBadge': 'Testimonials',
    'benefits.testimonialsTitle': 'What Our Clients Say',
    'benefits.testimonial1.quote': 'After implementing the recommendations, we saw a 42% increase in checkout conversion rate in just 3 weeks.',
    'benefits.testimonial1.author': 'Maria K.',
    'benefits.testimonial1.role': 'E-commerce Manager',
    'benefits.testimonial1.company': 'Fashion Brand',
    'benefits.testimonial2.quote': 'We saved weeks of work. The AI identified issues we had never noticed.',
    'benefits.testimonial2.author': 'George P.',
    'benefits.testimonial2.role': 'Digital Marketing Director',
    'benefits.testimonial2.company': 'Electronics Store',
    'benefits.testimonial3.quote': 'The white-label reporting helped us offer CRO services to our clients immediately.',
    'benefits.testimonial3.author': 'Nick M.',
    'benefits.testimonial3.role': 'Agency Owner',
    'benefits.testimonial3.company': 'Digital Agency',
    'benefits.ctaTitle': 'Start Today',
    'benefits.ctaSubtitle': 'Choose the plan that fits your needs and see the results',
    
    // Pricing Page
    'pricing.badge': 'Simple, transparent pricing',
    'pricing.title': 'Choose Your Plan',
    'pricing.subtitle': 'Get actionable CRO insights with our AI-powered audit. Buy audit packages with volume discounts - up to 50% off!',
    'pricing.selectQuantity': 'Select number of UX/CRO Audit:',
    'pricing.forAudits': 'for',
    'pricing.audits': 'audits',
    'pricing.audit': 'audit',
    'pricing.pages': 'pages',
    'pricing.page': 'page',
    'pricing.savings': 'Savings',
    'pricing.availableTypes': 'Available Audit Types',
    'pricing.mostPopular': 'Most Popular',
    'pricing.landingPage': 'Landing Page',
    'pricing.experience': 'Experience',
    'pricing.fullAnalysis': 'Full Audit',
    'pricing.landingDesc': 'Complete UX/CRO audit for a single page',
    'pricing.experienceDesc': 'Analyze up to 3 pages per audit',
    'pricing.fullDesc': 'Comprehensive audit for your key user flows',
    'pricing.monthly': 'Monthly',
    'pricing.yearly': 'Yearly',
    'pricing.perMonth': '/month',
    'pricing.free': 'Free',
    'pricing.popular': 'Popular',
    'pricing.enterprise': 'Enterprise',
    'pricing.contactUs': 'Contact Us',
    'pricing.enterpriseTitle': 'Enterprise & Agency',
    'pricing.enterpriseSubtitle': 'For large teams and agencies',
    'pricing.agencyAnnual': 'Agency Annual',
    'pricing.agencyDesc': 'Unlimited audits for agencies with multiple clients',
    'pricing.customPackage': 'Custom Package',
    'pricing.customDesc': 'Tailored solutions for enterprise needs',
    'pricing.contactSales': 'Contact Sales',
    'pricing.comparisonTitle': 'What Each Audit Includes',
    'pricing.faqTitle': 'Frequently Asked Questions',
    
    // Contact Page
    'contact.title': 'Get in Touch',
    'contact.subtitle': 'We\'re here to help you',
    'contact.name': 'Name',
    'contact.email': 'Email',
    'contact.company': 'Company',
    'contact.message': 'Message',
    'contact.send': 'Send',
    
    // FAQ
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Find answers to common questions',
    'faq.howCanWeHelp': 'How can we help?',
    'faq.notFound': 'Didn\'t find what you\'re looking for?',
    'faq.notFoundDesc': 'Our team is here to answer any question you have.',
    'faq.contactUs': 'Contact us',
    'faq.tryFree': 'Try Free',
    'faq.usefulLinks': 'Useful Links',
    'faq.pricingLink': 'Pricing',
    'faq.pricingDesc': 'See our plans',
    'faq.howItWorksLink': 'How It Works',
    'faq.howItWorksDesc': 'Learn more',
    'faq.agenciesLink': 'For Agencies',
    'faq.agenciesDesc': 'White-label solutions',
    'faq.freeAuditLink': 'Free Audit',
    'faq.freeAuditDesc': 'Start now',
    'faq.categories.general': 'General',
    'faq.categories.features': 'Features',
    'faq.categories.pricing': 'Pricing',
    'faq.categories.technical': 'Technical',
    'faq.categories.security': 'Security',
    
    // What is UX / CRO
    'whatIsUx.title': 'What is UX Design?',
    'whatIsCro.title': 'What is CRO?',
    'whatIsCro.badge': 'Conversion Optimization',
    'whatIsCro.heroTitle': 'What is',
    'whatIsCro.heroSubtitle': 'Conversion rate optimization (CRO) is the process of getting more of your existing visitors to buy, sign up or enquire. This guide covers the formula, benchmarks by industry, the four pillars and the mistakes that waste most CRO budgets.',
    'whatIsCro.scienceTitle': 'The Science of',
    'whatIsCro.scienceDesc1': 'CRO is a data-driven process that uses analytics, user research, and A/B testing to systematically improve your website results.',
    'whatIsCro.scienceDesc2': 'Instead of spending more on advertising, CRO helps you convert more existing visitors into customers.',
    'whatIsCro.formula': 'Conversion Rate Formula',
    'whatIsCro.processTitle': 'The process of',
    'whatIsCro.processSubtitle': 'A systematic approach to conversion optimization',
    'whatIsCro.realResults': 'Real Results',
    'whatIsCro.realResultsSubtitle': 'See how CRO can transform your results',
    'whatIsCro.techniquesTitle': 'Core techniques of',
    'whatIsCro.ctaTitle': 'Start CRO today',
    'whatIsCro.ctaSubtitle': 'EyeCaptain automates CRO audit and gives you instant recommendations',
    'whatIsCro.howItWorks': 'How It Works',
    
    // How It Works Page
    'howItWorks.badge': 'How It Works',
    'howItWorks.heroTitle': 'How an AI website audit works:',
    'howItWorks.heroTitleHighlight': 'from URL to CRO fixes',
    'howItWorks.heroSubtitle': 'EyeCaptain reads your whole site to learn what you sell, captures the page on desktop and mobile, measures what pulls attention, and returns a conversion audit with the changes in the order to make them.',
    'howItWorks.liveDemo': 'Live Demo',
    'howItWorks.seeHow': 'See how it',
    'howItWorks.works': 'works',
    'howItWorks.watchProcess': 'Watch the audit process in real time',
    'howItWorks.coreFeatures': 'Core Features',
    'howItWorks.toolsTitle': 'What makes EyeCaptain',
    'howItWorks.toolsHighlight': 'stand out',
    'howItWorks.toolsSubtitle': 'Combination of AI, Neuromarketing and CRO techniques for maximum results',
    'howItWorks.stepsTitle': '4 Simple',
    'howItWorks.stepsHighlight': 'Steps',
    'howItWorks.stepsSubtitle': 'From URL to complete CRO report in less than 5 minutes',
    'howItWorks.step1.title': 'Enter the URL',
    'howItWorks.step1.desc': 'Simply enter the page URL. EyeCaptain automatically crawls and takes screenshots.',
    'howItWorks.step2.title': 'AI Audit',
    'howItWorks.step2.desc': 'AI analyzes every aspect: UX, visual hierarchy, CTAs, forms, content, navigation with 100+ metrics.',
    'howItWorks.step3.title': 'Eye-Tracking Heatmaps',
    'howItWorks.step3.desc': 'We create predictive attention heatmaps showing where users look, on desktop and mobile.',
    'howItWorks.step4.title': 'Actionable Report',
    'howItWorks.step4.desc': 'Receive detailed PDF report with priorities, recommendations and implementation tips.',
    'howItWorks.analysisTypes': 'Audit Types',
    'howItWorks.analysisTypesSubtitle': 'Choose the audit type that fits your website',
    'howItWorks.whatWeAnalyze': 'What We Analyze',
    'howItWorks.whatWeAnalyzeSubtitle': 'In-depth audit of every aspect of your page',
    'howItWorks.aiTech': 'AI Technology',
    'howItWorks.aiTechSubtitle': 'Advanced technology for accurate results',
    'howItWorks.ctaTitle': 'Ready to Get Started?',
    'howItWorks.ctaSubtitle': 'Run your first audit for free and see what EyeCaptain can do for you.',
    'howItWorks.learnMore': 'Learn more',
    
    // Contact Page
    'contact.badge': 'Contact Us',
    'contact.heroTitle': 'Get in',
    'contact.heroTitleHighlight': 'Touch',
    'contact.heroSubtitle': 'Have questions? Want a demo? We\'re here to help!',
    'contact.formTitle': 'Send us a message',
    'contact.namePlaceholder': 'Your name',
    'contact.emailPlaceholder': 'you@company.com',
    'contact.companyPlaceholder': 'Your company',
    'contact.messagePlaceholder': 'How can we help you?',
    'contact.sending': 'Sending...',
    'contact.thankYou': 'Thank you!',
    'contact.thankYouMessage': 'We\'ll get back to you as soon as possible.',
    'contact.newMessage': 'New message',
    'contact.successToast': 'Your message was sent successfully!',
    'contact.infoTitle': 'Contact Information',
    'contact.phone': 'Phone',
    'contact.location': 'Location',
    'contact.locationValue': 'Athens, Greece',
    'contact.hours': 'Business Hours',
    'contact.hoursValue': 'Mon-Fri: 9:00-18:00',
    'contact.faqPreview': 'Frequently Asked Questions',
    'contact.faqQuestion1': 'How much does EyeCaptain cost?',
    'contact.faqQuestion2': 'How long does an audit take?',
    'contact.faqQuestion3': 'Do you support white-label reports?',
    'contact.viewAllFaq': 'View all questions',
    'contact.usefulLinks': 'Useful Links',
    
    // Auth
    'auth.login': 'Login',
    'auth.signup': 'Sign Up',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.forgotPassword': 'Forgot password?',
    'auth.noAccount': 'Don\'t have an account?',
    'auth.hasAccount': 'Already have an account?',
    'auth.signupNow': 'Sign up now',
    'auth.loginNow': 'Login',
    
    // Dashboard
    'dashboard.welcome': 'Welcome',
    'dashboard.projects': 'My Projects',
    'dashboard.newProject': 'New Project',
    'dashboard.audits': 'Audits',
    'dashboard.usage': 'Usage',
    
    // AI Predictive Attention Page
    'aiHeatmaps.badge': 'AI-Powered Eye Tracking',
    'aiHeatmaps.heroTitle1': 'See your page',
    'aiHeatmaps.heroTitle2': 'through the eyes',
    'aiHeatmaps.heroTitle3': 'of your users',
    'aiHeatmaps.heroSubtitle': 'AI Predictive Attention simulate how users see your page. Identify blind spots, improve visual hierarchy and increase conversions.',
    'aiHeatmaps.cta': 'Try Now',
    'aiHeatmaps.howItWorks': 'How it works',
    'aiHeatmaps.whyTitle': 'Why you need Heatmaps',
    'aiHeatmaps.benefitsTitle': 'Benefits of audit',
    'aiHeatmaps.benefitsSubtitle': '90% of communication is visual. Heatmaps show you what users actually see.',
    'aiHeatmaps.typesTitle': 'Types of Heatmaps',
    'aiHeatmaps.typesSubtitle': 'Each heatmap type reveals different information about user behavior.',
    'aiHeatmaps.processTitle': 'How it works',
    'aiHeatmaps.useCasesTitle': 'Heatmap Applications',
    'aiHeatmaps.ctaTitle': 'Start with AI Predictive Attention',
    'aiHeatmaps.ctaSubtitle': 'See where users look on your page',
    
    // Lead Generation Solution Page
    'leadgen.badge': 'Lead Generation CRO',
    'leadgen.heroTitle1': 'Skyrocket your',
    'leadgen.heroTitle2': 'Lead Conversion',
    'leadgen.heroTitle3': 'in your Funnel',
    'leadgen.heroSubtitle': '70% of websites lose leads daily because of significant UX issues.',
    'leadgen.cta': 'Start Now',
    'leadgen.howItWorks': 'See how it works',
    'leadgen.challengesTitle': 'Why you lose leads',
    'leadgen.challengesSubtitle': 'The most common areas that need improvement.',
    'leadgen.demoTitle': 'What CRO AI Powered Audit includes',
    'leadgen.demoSubtitle': 'Detailed scores for forms, CTAs, landing pages.',
    'leadgen.featuresTitle': 'What we analyze',
    'leadgen.resultsTitle': 'Results',
    'leadgen.formBestPractices': 'Form Best Practices',
    'leadgen.ctaTitle': 'Start Lead Gen Audit',
    'leadgen.ctaSubtitle': 'Increase leads with optimized UX',
    
    // Neuromarketing Page
    'neuro.badge': 'Psychology-Based Optimization',
    'neuro.heroTitle1': 'How your Site content',
    'neuro.heroTitle2': 'affects User psychology',
    'neuro.heroSubtitle': 'Neuromarketing: Understand how users think and design experiences that naturally lead to action.',
    'neuro.cta': 'Analyze your Page',
    'neuro.learnMore': 'Learn More',
    'neuro.principlesTitle': 'Neuromarketing Principles',
    'neuro.principlesSubtitle': 'How the brain is affected',
    'neuro.principlesDesc': '6 core principles that determine how users perceive and react.',
    'neuro.biasesTitle': 'Cognitive Biases',
    'neuro.biasesSubtitle': 'Psychological patterns that influence decisions.',
    'neuro.colorTitle': 'Color Psychology',
    'neuro.colorSubtitle': 'The psychology of colors',
    'neuro.persuasionTitle': 'Persuasion Elements',
    'neuro.ctaTitle': 'Apply Neuromarketing',
    'neuro.ctaSubtitle': 'EyeCaptain analyzes the psychological triggers on your page',
    
    // CRO Techniques Page
    'croTech.badge': 'Conversion Rate Optimization',
    'croTech.heroTitle1': 'Techniques that',
    'croTech.heroTitle2': 'increase conversions',
    'croTech.heroSubtitle': 'Learn the most effective CRO techniques used by top companies.',
    'croTech.cta': 'Start CRO Audit',
    'croTech.howItWorks': 'How it works',
    'croTech.techniquesTitle': 'Core CRO techniques',
    'croTech.quickWinsTitle': 'Quick wins',
    'croTech.quickWinsSubtitle': 'Changes you can make today.',
    'croTech.metricsTitle': 'Metrics we measure',
    'croTech.casesTitle': 'Real Case Studies',
    'croTech.frameworksTitle': 'CRO Frameworks',
    'croTech.ctaTitle': 'Start CRO Audit',
    'croTech.ctaSubtitle': 'Apply data-driven CRO to your page',
    
    // Competitor Analysis Page
    'competitor.badge': 'Competitor Audit',
    'competitor.title': 'Competitive Audit',
    'competitor.subtitle': 'Compare your website with up to 3 competitors simultaneously',
    'competitor.step1Title': 'Your Website',
    'competitor.step1Desc': 'Select an existing audit or create a quick audit',
    'competitor.existingProject': 'Existing Project',
    'competitor.quickAnalysis': 'Quick Audit',
    'competitor.selectProject': 'Select project...',
    'competitor.noCompletedProjects': 'No completed audits',
    'competitor.createQuick': 'Create Quick Audit',
    'competitor.analysisType': 'Audit Type',
    'competitor.businessName': 'Business Name',
    'competitor.websitePages': 'Website Pages',
    'competitor.addPage': 'Add Page',
    'competitor.step2Title': 'Competitors',
    'competitor.step2Tooltip': 'Each competitor must have the same pages as you for proper comparison.',
    'competitor.step2Desc': 'Fill in the corresponding URLs for each competitor',
    'competitor.competitorLabel': 'Competitor',
    'competitor.remove': 'Remove',
    'competitor.nameOptional': 'Name (optional)',
    'competitor.pagesSynced': 'Pages (synced with yours)',
    'competitor.addCompetitor': 'Add Competitor',
    'competitor.startAnalysis': 'Start Audit',
    'competitor.analyzing': 'Analyzing',
    'competitor.competitorSingular': 'competitor',
    'competitor.competitorPlural': 'competitors',
    'competitor.competitors': 'competitors',
    'competitor.infoText': 'Audit compares: Neuromarketing • CRO Audit • CRO Techniques',
    'competitor.errorSelectProject': 'Please select a project',
    'competitor.errorBusinessName': 'Please enter business name',
    'competitor.errorPageUrl': 'Please enter at least one page URL',
    'competitor.errorCompetitorUrl': 'Please enter at least one competitor URL',
    'competitor.errorLogin': 'You need to log in',
    'competitor.errorLoading': 'Error loading projects',
    'competitor.errorStart': 'Error starting audit',
    'competitor.successStart': 'Audit started!',
    'competitor.defaultCompetitor': 'Competitor',
    
    // Blog Page
    'blog.heroTitle': 'CRO and UX blog: the Captain\'s Log',
    'blog.heroSubtitle': 'Conversion rate optimization, heatmaps, analytics and landing page psychology, written from real audits.',
    'blog.allArticles': 'All articles',
    'blog.articlesCount': 'articles',
    'blog.popularArticles': 'Popular Articles',
    'blog.noArticles': 'No articles available yet.',
    'blog.noTopicArticles': 'No articles found for',
    'blog.newsletter': 'Newsletter',
    'blog.newsletterDesc': 'Get new articles in your email',
    'blog.comingSoon': 'Coming Soon',
    'blog.topicsFilter': 'Topics:',
    'blog.all': 'All',
    
    // Signup Page
    'signup.badge': 'AI-Powered CRO',
    'signup.title': 'Optimize your conversions',
    'signup.subtitle': 'Get actionable insights to increase your sales and leads.',
    'signup.whatYouGet': 'What you get',
    'signup.secureSignup': 'Secure signup with encryption',
    'signup.createAccount': 'Create Account',
    'signup.fillDetails': 'Fill in your details to get started',
    'signup.brandName': 'Business / Brand Name',
    'signup.industry': 'Industry',
    'signup.selectIndustry': 'Select industry',
    'signup.businessType': 'Business Type',
    'signup.email': 'Email',
    'signup.password': 'Password',
    'signup.passwordMin': 'At least 8 characters',
    'signup.confirmPassword': 'Confirm Password',
    'signup.repeatPassword': 'Repeat password',
    'signup.passwordMismatch': 'Passwords do not match',
    'signup.creating': 'Creating...',
    'signup.or': 'or',
    'signup.signupAsAgency': 'Sign up as Agency',
    'signup.haveAccount': 'Already have an account?',
    'signup.login': 'Log in',
    'signup.checkEmail': 'Check your email!',
    'signup.emailSent': 'We sent a confirmation email to',
    'signup.clickLink': 'Click the link to complete your registration.',
    'signup.backToLogin': 'Back to login',
    'signup.fillEmail': 'Please enter your email',
    'signup.fillBrandName': 'Please enter your business name',
    'signup.selectIndustryError': 'Please select an industry',
    'signup.selectBusinessType': 'Please select a business type',
    'signup.passwordTooShort': 'Password must be at least 8 characters',
    'signup.passwordsDontMatch': 'Passwords do not match',
    'signup.fillRecaptcha': 'Please complete the reCAPTCHA',
    'signup.emailAlreadyUsed': 'This email is already in use. Try logging in.',
    'signup.signupError': 'Error during signup',
    
    // Agency Signup Page
    'agencySignup.badge': 'For Agencies',
    'agencySignup.title': 'Upgrade your services with AI CRO Audit',
    'agencySignup.subtitle': 'Offer your clients data-driven CRO recommendations powered by AI.',
    'agencySignup.benefits': 'Agency Benefits',
    'agencySignup.createAccount': 'Create Agency Account',
    'agencySignup.fillDetails': 'Fill in your details to get started',
    'agencySignup.agencyName': 'Agency Name',
    'agencySignup.agencyNamePlaceholder': 'e.g. Digital Marketing Pro',
    'agencySignup.fillAgencyName': 'Please enter your agency name',
    'agencySignup.feature1': 'Advanced CRO Audit in under 5 minutes',
    'agencySignup.feature2': 'Client Folders to organize your projects',
    'agencySignup.feature3': 'Priority Support with rapid response',
    'agencySignup.feature4': 'Dedicated Account Manager',
    'agencySignup.feature5': 'White-Label Reports with your branding',
    'agencySignup.feature6': 'Exclusive offers for Agencies',
    
    // Onboarding Page
    'onboarding.skip': 'Skip',
    'onboarding.welcome': 'Welcome!',
    'onboarding.letsGetStarted': 'Let\'s set up your account',
    'onboarding.continue': 'Continue',
    'onboarding.back': 'Back',
    'onboarding.profileSaved': 'Profile saved!',
    'onboarding.saveError': 'Save error',
    'onboarding.firstName': 'First Name',
    'onboarding.lastName': 'Last Name',
    'onboarding.websiteDomain': 'Website Domain',
    'onboarding.agencyName': 'Agency Name',
    'onboarding.agencyType': 'Agency Type',
    'onboarding.teamSize': 'Team Size',
    'onboarding.clientCount': 'Number of Clients',
    'onboarding.goals': 'Goals',
    'onboarding.selectAtLeastOne': 'Select at least one goal',
    'onboarding.fillFirstLast': 'Please enter first and last name',
    'onboarding.fillDomain': 'Please enter your website domain',
    'onboarding.fillAgencyName': 'Please enter agency name',
    'onboarding.selectAgencyType': 'Please select agency type',
    'onboarding.selectTeamSize': 'Please select team size',
    'onboarding.selectClientCount': 'Please select number of clients',
    'onboarding.selectGoal': 'Please select at least one goal',
    'onboarding.skipSubscription': 'You can activate your subscription later from your account.',
    
    // Account Page
    'account.profile': 'Profile',
    'account.plans': 'Plans & Subscription',
    'account.billing': 'Billing',
    'account.team': 'Team',
    'account.referrals': 'Referrals',
    'account.dataExport': 'Data Export',
    'account.logout': 'Log Out',
    'account.profileSaved': 'Profile saved successfully',
    'account.profileError': 'Error saving profile',
    'account.logoUploaded': 'Logo uploaded successfully',
    'account.logoError': 'Error uploading logo',
    'account.agencyName': 'Agency / Brand Name',
    'account.logo': 'Logo',
    'account.uploadLogo': 'Upload Logo',
    'account.primaryColor': 'Primary Color',
    'account.secondaryColor': 'Secondary Color',
    'account.save': 'Save',
    'account.saving': 'Saving...',
    'account.language': 'Language',
    'account.languageDesc': 'Select the language for the interface',
    'account.greek': 'Greek',
    'account.english': 'English',
    
    // Quick Actions
    'account.quickActions.managePayment': 'Manage Payment',
    'account.quickActions.downloadInvoices': 'Download Invoices',
    'account.quickActions.addCredits': 'Add Credits',
    'account.quickActions.upgrade': 'Upgrade',
    
    // Account Sidebar
    'account.sidebar.subscription': 'Subscription',
    'account.sidebar.plansAndBilling': 'Plans & Billing',
    'account.sidebar.invoiceDetails': 'Invoice Details',
    'account.sidebar.account': 'Account',
    'account.sidebar.profile': 'Profile',
    'account.sidebar.teamMembers': 'Team Members',
    'account.sidebar.preferences': 'Preferences',
    'account.sidebar.security': 'Security',
    'account.sidebar.passwordAnd2FA': 'Password & 2FA',
    'account.sidebar.apiKeys': 'API Keys',
    'account.sidebar.notifications': 'Notifications',
    'account.sidebar.integrations': 'Integrations',
    'account.sidebar.connectedApps': 'Connected Apps',
    'account.sidebar.whitelabel': 'White Label',
    'account.sidebar.data': 'Data',
    'account.sidebar.usageAnalytics': 'Usage Analytics',
    'account.sidebar.referralProgram': 'Referral Program',
    'account.sidebar.exportData': 'Export Data',
    'account.sidebar.activityLog': 'Activity Log',
    'account.sidebar.danger': 'Danger Zone',
    'account.sidebar.deleteAccount': 'Delete Account',
    
    // Account Hero
    'account.hero.auditsUsed': 'Audits Used',
    'account.hero.remaining': 'Remaining Pages',
    'account.hero.renewsOn': 'Renews On',
    'account.hero.renews': 'Renews',
    'account.hero.month': 'month',
    'account.hero.noPlan': 'No active plan',
    'account.hero.upgradeCta': 'Upgrade to unlock all features',
    'account.hero.seePlans': 'See Plans',
    
    // Account Sidebar Logout
    'account.sidebar.logout': 'Log Out',
    
    // Quick Actions Extra
    'account.quickActions.portalError': 'Error opening payment portal',
    
    // Delete Account
    'account.delete.confirmRequired': 'Please confirm deletion',
    'account.delete.title': 'Delete Account',
    'account.delete.subtitle': 'Permanently delete your account and all your data',
    'account.delete.warningTitle': 'This action is irreversible',
    'account.delete.warning1': 'All projects and audits will be deleted',
    'account.delete.warning2': 'Your subscription will be canceled',
    'account.delete.warning3': 'All data will be permanently lost',
    'account.delete.warning4': 'No recovery is possible',
    'account.delete.acknowledgeLabel': 'I understand that this action is irreversible and all my data will be deleted',
    'account.delete.typeToConfirm': 'Type DELETE to confirm',
    'account.delete.typeDeleteHint': 'Type "DELETE" to enable the delete button',
    'account.delete.button': 'Delete Account',
    'account.delete.dialogTitle': 'Are you absolutely sure?',
    'account.delete.dialogDescription': 'This will permanently delete your account and all related data. This action cannot be undone.',
    'account.delete.cancel': 'Cancel',
    'account.delete.confirmButton': 'Yes, delete account',
    'account.delete.requestSent': 'Account deletion request sent',
    'account.delete.error': 'Error deleting account',
    'account.delete.gdprNote': 'According to GDPR, you have the right to delete your data',
    
    // E-commerce Solution Page
    'ecommerce.badge': 'E-commerce CRO Audit',
    'ecommerce.heroTitle1': 'E-commerce CRO Audit',
    'ecommerce.heroTitle2': 'for Product Pages,',
    'ecommerce.heroTitle3': 'Cart and Checkout',
    'ecommerce.heroSubtitle': 'Find where shoppers drop off between the product page and the order confirmation. Works on Shopify, WooCommerce, Magento and custom stores, with fixes ranked by revenue impact.',
    'ecommerce.startNow': 'Start Now',
    'ecommerce.seeHowItWorks': 'See how it works',
    'ecommerce.conversionFunnel': 'Conversion Funnel',
    'ecommerce.lossPerStep': 'Loss per step',
    'ecommerce.totalLoss': 'loss from Homepage to Purchase',
    'ecommerce.challengesBadge': 'User Experience Issues',
    'ecommerce.challengesTitle': 'Users',
    'ecommerce.challengesTitleHighlight': 'abandon',
    'ecommerce.challengesDesc': 'The most common areas that need improvement on e-commerce sites and how they affect your revenue.',
    'ecommerce.cartAbandonment': 'Cart Abandonment',
    'ecommerce.avgAbandonment': 'Average cart abandonment rate',
    'ecommerce.demoBadge': 'Demo Report Preview',
    'ecommerce.demoTitle': 'What\'s included in',
    'ecommerce.demoTitleHighlight': 'CRO AI Powered',
    'ecommerce.demoDesc': 'Detailed scores for every critical point of your e-shop, with actionable recommendations for improvement.',
    'ecommerce.demoFeature1': 'Heatmaps for each page',
    'ecommerce.demoFeature2': 'Friction point audit',
    'ecommerce.demoFeature3': 'Priority action plan',
    'ecommerce.demoFeature4': 'Revenue impact estimation',
    'ecommerce.featuresTitle': 'What we',
    'ecommerce.featuresHighlight': 'analyze',
    'ecommerce.resultsBadge': 'Results',
    'ecommerce.resultsTitle': 'Average',
    'ecommerce.resultsHighlight': 'results',
    'ecommerce.pagesTitle': 'Pages we',
    'ecommerce.pagesHighlight': 'analyze',
    'ecommerce.ctaTitle': 'Ready to increase your',
    'ecommerce.ctaHighlight': 'sales',
    'ecommerce.ctaDesc': 'Start with a complete CRO audit of your e-shop today',
    'ecommerce.talkToExpert': 'Talk to an expert',
    
    // What is UX Page
    'whatIsUx.badge': 'Learn About UX',
    'whatIsUx.heroTitle': 'What is',
    'whatIsUx.heroTitleHighlight': 'User Experience (UX)',
    'whatIsUx.heroSubtitle': 'User experience is the total of emotions, perceptions and reactions a user has when interacting with your website.',
    'whatIsUx.whyTitle': 'Why UX is',
    'whatIsUx.whyHighlight': 'critical',
    'whatIsUx.whyDesc': 'User Experience is not just about how beautiful a website looks. It\'s the overall user experience - from the moment they enter the site until they complete the purchase.',
    'whatIsUx.check1': 'How easily they find what they\'re looking for',
    'whatIsUx.check2': 'If buttons are clear and accessible',
    'whatIsUx.check3': 'If navigation is intuitive',
    'whatIsUx.check4': 'If the site loads fast',
    'whatIsUx.check5': 'If it inspires trust',
    'whatIsUx.check6': 'If it works properly on mobile',
    'whatIsUx.impactTitle': 'UX Impact Statistics',
    'whatIsUx.impact1.stat': '88%',
    'whatIsUx.impact1.label': 'of users don\'t return after a bad experience',
    'whatIsUx.impact2.stat': '400%',
    'whatIsUx.impact2.label': 'conversion increase with improved UX',
    'whatIsUx.impact3.stat': '3.75x',
    'whatIsUx.impact3.label': 'more ROI from UX investments',
    'whatIsUx.impact4.stat': '70%',
    'whatIsUx.impact4.label': 'of failed projects are due to poor UX',
    'whatIsUx.principlesTitle': 'Core Principles of',
    'whatIsUx.principlesHighlight': 'UX',
    'whatIsUx.principlesDesc': 'These are the fundamental principles that define an excellent user experience',
    'whatIsUx.principle1.title': 'User-Centered Design',
    'whatIsUx.principle1.desc': 'Design that puts user needs at the center of every decision.',
    'whatIsUx.principle2.title': 'Emotional Connection',
    'whatIsUx.principle2.desc': 'Creating positive emotions that lead to loyalty.',
    'whatIsUx.principle3.title': 'Usability',
    'whatIsUx.principle3.desc': 'How easily the user can achieve their goals.',
    'whatIsUx.principle4.title': 'Accessibility',
    'whatIsUx.principle4.desc': 'Accessibility for everyone, regardless of device or abilities.',
    'whatIsUx.vsTitle': 'UX vs UI: The',
    'whatIsUx.vsHighlight': 'Difference',
    'whatIsUx.ux.title': 'User Experience (UX)',
    'whatIsUx.ux.desc': 'The overall user experience. How they feel, how easily they achieve their goals.',
    'whatIsUx.ux.score': 'EyeCaptain UX Score',
    'whatIsUx.ui.title': 'User Interface (UI)',
    'whatIsUx.ui.desc': 'The visual appearance. Colors, typography, buttons, images, animations.',
    'whatIsUx.ui.score': 'EyeCaptain UI Score',
    'whatIsUx.ctaTitle': 'Want us to analyze your UX?',
    'whatIsUx.ctaDesc': 'EyeCaptain automatically analyzes user experience with AI',
    'whatIsUx.ctaHowItWorks': 'How It Works',
    
    // Analysis Page
    'analysis.badge': 'AI-Powered CRO Audit',
    'analysis.heroTitle': 'How EyeCaptain',
    'analysis.heroTitleHighlight': 'Analyzes Your Site',
    'analysis.heroDesc': 'AI-powered audit with eye-tracking heatmaps, neuromarketing insights and actionable CRO recommendations',
    'analysis.freeDemo': 'Free Demo',
    'analysis.typesBadge': 'Audit Types',
    'analysis.typesTitle': '6 Types of',
    'analysis.typesHighlight': 'Audit',
    'analysis.typesDesc': 'Every page is analyzed from 6 different perspectives for complete CRO coverage',
    'analysis.avgImprovement': 'Avg. Improvement',
    'analysis.heatmapBadge': 'AI Eye-Tracking',
    'analysis.heatmapTitle': 'Predictive',
    'analysis.heatmapHighlight': 'Heatmaps',
    'analysis.heatmapDesc': 'See where your visitors look without real users, on desktop and mobile',
    'analysis.attention': 'Attention:',
    'analysis.high': 'High',
    'analysis.medium': 'Medium',
    'analysis.low': 'Low',
    'analysis.clarityScore': 'Clarity Score',
    'analysis.ctaVisibility': 'CTA Visibility',
    'analysis.timeToCta': 'Time to CTA',
    'analysis.cognitiveLoad': 'Cognitive Load',
    'analysis.heatmapFeature1': 'Attention Maps',
    'analysis.heatmapFeature1Desc': 'The first 3-5 seconds',
    'analysis.heatmapFeature2': 'Clarity Score',
    'analysis.heatmapFeature2Desc': 'Clean vs cluttered design',
    'analysis.heatmapFeature3': 'Visual Hierarchy',
    'analysis.heatmapFeature3Desc': 'CTA visibility',
    'analysis.heatmapFeature4': 'Cognitive Load',
    'analysis.heatmapFeature4Desc': 'Load measurement',
    'analysis.stepsTitle': '4 Simple',
    'analysis.stepsHighlight': 'Steps',
    'analysis.stepsDesc': 'From URL to complete CRO report in less than 5 minutes',
    'analysis.step1': 'Upload URL',
    'analysis.step1Desc': 'Enter your page URL',
    'analysis.step2': 'AI Processing',
    'analysis.step2Desc': 'Audit with 100+ CRO metrics',
    'analysis.step3': 'Expert Review',
    'analysis.step3Desc': 'Prioritized recommendations',
    'analysis.step4': 'PDF Report',
    'analysis.step4Desc': 'Actionable insights report',
    'analysis.ctaTitle': 'Ready to increase your conversions?',
    'analysis.ctaDesc': 'Start today with a plan that fits your needs',
    
    // Agencies Page
    'agencies.badge': 'For Agencies',
    'agencies.heroTitle1': 'White Label CRO Audit Tool',
    'agencies.heroTitle2': 'for Agencies',
    'agencies.heroDesc': 'Deliver a full UX and conversion audit to every client in minutes, under your own brand. Multi-client workspaces, PDF reports with your logo, and pricing built for service teams. No in-house CRO specialist required.',
    'agencies.startNow': 'Start Now',
    'agencies.bookDemo': 'Book a Demo',
    'agencies.pdfReady': 'PDF Ready for Client',
    'agencies.pages': 'pages',
    'agencies.instantDownload': 'Instant PDF download - Ready for your client',
    'agencies.servicesBadge': 'What We Provide',
    'agencies.servicesTitle': 'Complete',
    'agencies.servicesHighlight': 'CRO/UX AI Audit',
    'agencies.servicesSubtitle': 'for Your Clients',
    'agencies.servicesDesc': 'A complete suite of tools to deliver value to your clients.',
    'agencies.feature1.title': 'AI-Powered Eye-Tracking Audit',
    'agencies.feature1.desc': 'Eye-tracking simulation using AI to see where users actually look on your client\'s site. Identify blind spots and missed opportunities.',
    'agencies.feature2.title': 'Conversion Rate Optimization',
    'agencies.feature2.desc': 'Get specific, actionable recommendations to improve conversion rate. Each recommendation is based on data-driven insights.',
    'agencies.feature3.title': 'UX/UI Audit',
    'agencies.feature3.desc': 'Complete usability audit with navigation, layout, visual hierarchy and user flow evaluation. Identify friction points costing conversions.',
    'agencies.feature4.title': 'Neuromarketing Insights',
    'agencies.feature4.desc': 'Understand how users are psychologically affected. Analyze color psychology, cognitive load, trust signals and persuasion elements.',
    'agencies.feature5.title': 'Priority Scoring',
    'agencies.feature5.desc': 'Each recommendation comes with a priority score (Impact vs Effort). Know exactly where to start for maximum results.',
    'agencies.feature6.title': 'Multi-Page Audit',
    'agencies.feature6.desc': 'Analyze complete user journeys - from landing page to checkout. Find weak points at every funnel step.',
    'agencies.benefit1.title': 'Increase Agency Revenue',
    'agencies.benefit1.desc': 'Offer CRO/UX audits as a premium service without hiring specialists. An audit you sell for €500+ costs you just minutes.',
    'agencies.benefit2.title': 'Deliver More Value to Clients',
    'agencies.benefit2.desc': 'Actionable insights lead to measurable results. Your clients see real ROI and stay loyal to your agency.',
    'agencies.deliveryBadge': 'Delivery',
    'agencies.deliveryTitle': 'Professional Deliverables',
    'agencies.delivery1.title': 'Professional PDF Report',
    'agencies.delivery1.desc': 'Detailed 30-50 page PDF report with executive summary, detailed audit and actionable recommendations. Ready to send directly to your client.',
    'agencies.delivery1.highlight': 'With White-Label capability',
    'agencies.delivery2.title': 'Client Presentation Ready',
    'agencies.delivery2.desc': 'Reports are designed to impress. Clear visualizations, heatmaps and before/after comparisons that make findings crystal clear.',
    'agencies.delivery3.title': 'Multi-Language Support',
    'agencies.delivery3.desc': 'Create reports in Greek, English or other languages for your international clients.',
    'agencies.delivery4.title': 'Instant Download',
    'agencies.delivery4.desc': 'Download the PDF report instantly. In minutes you have a complete deliverable ready for your client.',
    'agencies.useCasesBadge': 'Use Cases',
    'agencies.useCasesTitle': 'How to Use EyeCaptain',
    'agencies.useCase1.title': 'New Client Pitch',
    'agencies.useCase1.desc': 'Create a quick audit of your prospect\'s site. Show them what they\'re missing and win the project with a data-driven approach.',
    'agencies.useCase1.scenario': 'Pitch Meeting',
    'agencies.useCase2.title': 'Campaign Optimization',
    'agencies.useCase2.desc': 'Before starting a campaign, analyze the landing page. Make sure the traffic you bring will convert.',
    'agencies.useCase2.scenario': 'Pre-Campaign',
    'agencies.useCase3.title': 'Monthly Retainer Value',
    'agencies.useCase3.desc': 'Add quarterly CRO audits to your retainer package. Show continuous value and justify the monthly fee.',
    'agencies.useCase3.scenario': 'Retainer Clients',
    'agencies.useCase4.title': 'Upsell Opportunity',
    'agencies.useCase4.desc': 'Use the report to propose implementation services. The audit becomes a gateway for development work.',
    'agencies.useCase4.scenario': 'Upselling',
    'agencies.howItWorksBadge': 'How It Works',
    'agencies.howItWorksTitle': 'How It Works',
    'agencies.step1.title': 'Create Project',
    'agencies.step1.desc': 'Enter your client details and pages to analyze',
    'agencies.step2.title': 'AI Audit',
    'agencies.step2.desc': 'EyeCaptain analyzes each page with AI eye-tracking and UX evaluation',
    'agencies.step3.title': 'Review Results',
    'agencies.step3.desc': 'View findings in dashboard with heatmaps and detailed insights',
    'agencies.step4.title': 'Export PDF',
    'agencies.step4.desc': 'Download the professional PDF report ready for your client',
    'agencies.testimonialsBadge': 'Testimonials',
    'agencies.testimonialsTitle': 'What Agencies Say',
    
    // Custom Solution Page
    'custom.badge': 'Custom CRO Audit',
    'custom.heroTitle1': 'Audit',
    'custom.heroTitle2': 'Tailored to Your Business',
    'custom.heroDesc': 'Have a unique business model? We create custom audit criteria specifically for your needs.',
    'custom.contactUs': 'Contact Us',
    'custom.seePackages': 'See Packages',
    'custom.solutionsBadge': 'Custom Solutions',
    'custom.solutionsTitle': 'For Every',
    'custom.solutionsHighlight': 'Business Model',
    'custom.solutionsDesc': 'We tailor the audit to the specific needs of each industry',
    'custom.useCase1.title': 'SaaS Platforms',
    'custom.useCase1.desc': 'Onboarding flows, feature adoption, pricing pages',
    'custom.useCase2.title': 'Marketplace',
    'custom.useCase2.descEl': 'Two-sided platforms with buyers & sellers',
    'custom.useCase2.descEn': 'Two-sided platforms with buyers & sellers',
    'custom.useCase3.title': 'Portfolio Sites',
    'custom.useCase3.desc': 'Creative agencies, freelancers, artists',
    'custom.useCase4.title': 'Media & Content',
    'custom.useCase4.desc': 'Publishers, blogs, news sites',
    'custom.processBadge': 'Process',
    'custom.processTitle': 'How It',
    'custom.processHighlight': 'Works',
    'custom.step1.title': 'Discovery Call',
    'custom.step1.desc': 'We understand your business model and goals',
    'custom.step2.title': 'Custom Criteria',
    'custom.step2.desc': 'We create custom audit criteria for your industry',
    'custom.step3.title': 'Deep Audit',
    'custom.step3.desc': 'We run the audit focused on your KPIs',
    'custom.step4.title': 'Strategy Session',
    'custom.step4.desc': 'Findings presentation and implementation roadmap',
    'custom.customizeTitle': 'What We',
    'custom.customizeHighlight': 'Customize',
    'custom.industriesTitle': 'Industries We',
    'custom.industriesHighlight': 'Support',
    'custom.industriesDesc': 'We have experience in many different industries',
    'custom.ctaTitle': 'Have',
    'custom.ctaHighlight': 'Special Needs',
    'custom.ctaDesc': 'Talk to us to create a custom audit for your business',
    
    // Account Settings
    'account.settings': 'Account Settings',
    'account.backToSettings': 'Back to Settings',
    'account.auditsAvailable': 'audits available',
    
    // Plans & Usage
    'account.plans.title': 'Your Plan',
    'account.plans.subtitle': 'Subscription and usage information',
    'account.plans.active': 'Active',
    'account.plans.monthlySubscription': 'Monthly subscription',
    'account.plans.perMonth': '/month',
    'account.plans.auditsUsed': 'Audits used',
    'account.plans.renewal': 'Renewal',
    'account.plans.noPlan': 'No active plan',
    'account.plans.seePlans': 'See our plans',
    
    // Profile
    'account.profile.title': 'Profile Details',
    'account.profile.subtitle': 'Manage basic account details',
    'account.profile.name': 'Name',
    'account.profile.email': 'Email',
    'account.profile.agencyName': 'Agency Name',
    'account.profile.companyName': 'Company Name',
    'account.profile.logo': 'Logo',
    'account.profile.save': 'Save',
    'account.profile.saving': 'Saving...',
    'account.profile.saveSuccess': 'Profile saved successfully',
    'account.profile.saveError': 'Error saving profile',
    'account.profile.logoUploaded': 'Logo uploaded successfully',
    'account.profile.logoError': 'Error uploading logo',
    
    // Invoice/Billing
    'account.invoice.paymentManagement': 'Payment Management',
    'account.invoice.paymentManagementDesc': 'View invoices, change payment method or cancel subscriptions',
    'account.invoice.goToPortal': 'Go to Stripe Customer Portal to manage your payments.',
    'account.invoice.openPortal': 'Open Customer Portal',
    'account.invoice.portalError': 'Error opening portal',
    'account.invoice.billingDetails': 'Billing Details',
    'account.invoice.billingDetailsDesc': 'Fill in the details for invoice issuance',
    'account.invoice.companyName': 'Company Name',
    'account.invoice.vatNumber': 'VAT Number',
    'account.invoice.taxOffice': 'Tax Office',
    'account.invoice.phone': 'Phone',
    'account.invoice.address': 'Address',
    'account.invoice.city': 'City',
    'account.invoice.postalCode': 'Postal Code',
    'account.invoice.country': 'Country',
    'account.invoice.saveDetails': 'Save Details',
    'account.invoice.saveSuccess': 'Billing details saved to Stripe',
    'account.invoice.saveError': 'Error saving details',
    'account.invoice.vatNotFound': 'VAT number not found',
    'account.invoice.viesSuccess': 'Details filled from VIES',
    'account.invoice.viesError': 'Error looking up VAT',
    'account.invoice.vatInvalid9digits': 'VAT number must have 9 digits',
    'account.invoice.vatInvalidChecksum': 'Invalid VAT number (wrong check digit)',
    'account.invoice.enterVat': 'Please enter VAT number',
    
    // Security
    'account.security.2faTitle': 'Two-Factor Authentication (2FA)',
    'account.security.2faSubtitle': 'Add an extra layer of security to your account',
    'account.security.2faEnabled': '2FA is enabled',
    'account.security.2faDisabled': '2FA is not enabled',
    'account.security.2faEnabledDesc': 'You receive a verification code to your email',
    'account.security.2faDisabledDesc': 'Enable it for extra security',
    'account.security.2faToggleOn': '2FA setup will be enabled on next login',
    'account.security.2faToggleOff': '2FA has been disabled',
    'account.security.passwordChange': 'Change Password',
    'account.security.passwordChangeDesc': 'Update your account password',
    'account.security.newPassword': 'New Password',
    'account.security.enterNewPassword': 'Enter new password',
    'account.security.minChars': 'At least 8 characters',
    'account.security.confirmPassword': 'Confirm Password',
    'account.security.confirmNewPassword': 'Confirm the new password',
    'account.security.changePassword': 'Change Password',
    'account.security.changing': 'Changing...',
    'account.security.fillAllFields': 'Please fill all fields',
    'account.security.passwordsDontMatch': 'Passwords do not match',
    'account.security.passwordMinLength': 'Password must be at least 8 characters',
    'account.security.passwordChanged': 'Password changed successfully',
    'account.security.passwordChangeError': 'Error changing password',
    'account.security.trustedDevices': 'Trusted Devices',
    'account.security.trustedDevicesDesc': 'Manage devices with saved login',
    'account.security.refresh': 'Refresh',
    'account.security.noDevices': 'No saved devices',
    'account.security.noDevicesDesc': 'Devices will appear here when you select "Remember device" during login',
    'account.security.unknownDevice': 'Unknown Device',
    'account.security.expired': 'Expired',
    'account.security.expires': 'Expires',
    'account.security.addedAgo': 'Added',
    'account.security.removeDevice': 'Remove Device',
    'account.security.removeDeviceConfirm': 'Are you sure you want to remove this device? You will need to enter the 2FA code next time you log in from it.',
    'account.security.deviceRemoved': 'Device removed',
    'account.security.deviceRemoveError': 'Error removing device',
    'account.security.currentSession': 'Current Session',
    'account.security.currentSessionDesc': 'Information about your current connection',
    'account.security.activeSession': 'Active Session',
    'account.security.thisDevice': 'This device',
    'account.security.mobile': 'Mobile',
    'account.security.desktop': 'Desktop',
    'account.security.lastActivity': 'Last activity: Now',
    'account.security.logoutAll': 'Sign out from all devices',
    
    // Notifications
    'account.notifications.title': 'Notification Preferences',
    'account.notifications.subtitle': 'Manage which emails you want to receive',
    'account.notifications.billingSection': 'Billing & Payments',
    'account.notifications.securitySection': 'Security',
    'account.notifications.updatesSection': 'Updates & Marketing',
    'account.notifications.billingAlerts': 'Billing Alerts',
    'account.notifications.billingAlertsDesc': 'Updates about payments, invoices and subscription renewals',
    'account.notifications.paymentReminders': 'Payment Reminders',
    'account.notifications.paymentRemindersDesc': 'Reminder before subscription expires or payment failure',
    'account.notifications.securityAlerts': 'Security Alerts',
    'account.notifications.securityAlertsDesc': 'Notifications for new logins and account changes',
    'account.notifications.productUpdates': 'Product Updates',
    'account.notifications.productUpdatesDesc': 'New features, improvements and announcements',
    'account.notifications.weeklyDigest': 'Weekly Digest',
    'account.notifications.weeklyDigestDesc': 'Activity summary and insights every week',
    'account.notifications.marketingEmails': 'Marketing Emails',
    'account.notifications.marketingEmailsDesc': 'Promotional offers, tips and educational content',
    'account.notifications.saveChanges': 'Save Changes',
    'account.notifications.saved': 'Saved',
    'account.notifications.saveSuccess': 'Preferences saved',
    'account.notifications.saveError': 'Error saving',
    
    // Activity Log
    'account.activity.title': 'Activity History',
    'account.activity.subtitle': 'Recent actions on your account',
    'account.activity.refresh': 'Refresh',
    'account.activity.noActivities': 'No recent activities',
    'account.activity.paymentCompleted': 'Payment completed',
    'account.activity.paymentPending': 'Payment pending',
    'account.activity.successful': 'Successful',
    'account.activity.waiting': 'Waiting',
    'account.activity.newProject': 'New Project',
    'account.activity.analysisCompleted': 'Audit completed',
    'account.activity.login': 'Login',
    'account.activity.loginSuccess': 'Successful account login',
    
    // Referral Program
    'account.referral.title': 'Referral Program',
    'account.referral.subtitle': 'Earn free audits by referring friends',
    'account.referral.totalReferrals': 'Total Referrals',
    'account.referral.successful': 'Successful',
    'account.referral.activeRewards': 'Active Rewards',
    'account.referral.yourLink': 'Your Unique Link',
    'account.referral.shareAndEarn': 'Share and earn',
    'account.referral.linkCopied': 'Link copied!',
    'account.referral.discountCode': 'Discount Code',
    'account.referral.discountForFriends': '40% discount for new users',
    'account.referral.codeCopied': 'Discount code copied!',
    'account.referral.friendsGet': 'Your friends get',
    'account.referral.discountFirst': '40% discount',
    'account.referral.onFirstPurchase': 'on their first purchase (new accounts only).',
    'account.referral.howItWorks': 'How It Works',
    'account.referral.step1.title': '1. Share',
    'account.referral.step1.desc': 'Send your unique link to friends or colleagues',
    'account.referral.step2.title': '2. Complete Signup',
    'account.referral.step2.desc': 'Friend verifies email, completes profile & runs an audit → You earn 10 credits',
    'account.referral.step3.title': '3. Purchase',
    'account.referral.step3.desc': 'Friend buys any package → You earn 100 credits!',
    'account.referral.step3.highlight': '100 credits',
    'account.referral.step3.duration': 'per purchase!',
    'account.referral.exampleReward': 'Rewards',
    'account.referral.exampleRewardDesc': 'Successful signup = 10 credits | Package purchase = 100 credits | Friend = 40% off',
    'account.referral.activeRewardsTitle': 'Credits Earned',
    'account.referral.activeRewardsDesc': 'Credits you have earned from referrals',
    'account.referral.pagesPerAudit': 'pages per audit',
    'account.referral.expiresOn': 'Expires',
    'account.referral.earnedOn': 'Earned',
    'account.referral.referralHistory': 'Referral History',
    'account.referral.inProgress': 'in progress',
    'account.referral.noReferrals': 'No referrals yet',
    'account.referral.noReferralsDesc': 'Share your link and start earning credits!',
    'account.referral.referralId': 'Referral',
    'account.referral.completedOn': 'Completed',
    'account.referral.pending': 'Pending',
    'account.referral.registered': 'Registered',
    'account.referral.converted': 'Completed',
    'account.referral.referralExpired': 'Expired',
    
    // Data Export
    'account.export.title': 'Data Export (GDPR)',
    'account.export.subtitle': 'Download all your data according to GDPR',
    'account.export.includes': 'What is included:',
    'account.export.profileData': 'Account details and settings',
    'account.export.projectsData': 'All your projects and pages',
    'account.export.analysisData': 'CRO audit results',
    'account.export.paymentsData': 'Payment history',
    'account.export.referralsData': 'Referral history',
    'account.export.exportJson': 'Export JSON',
    'account.export.jsonDesc': 'Complete data structure',
    'account.export.exportCsv': 'Export CSV',
    'account.export.csvDesc': 'Compatible with Excel/Sheets',
    'account.export.gdprNote': 'According to GDPR Article 20, you have the right to receive a copy of your personal data.',
    'account.export.success': 'Data exported successfully to',
    'account.export.error': 'Error exporting data',
    
    // Usage Analytics
    'account.analytics.totalProjects': 'Total Projects',
    'account.analytics.pagesAnalyzed': 'Pages Analyzed',
    'account.analytics.totalAudits': 'Total Audits',
    'account.analytics.auditsThisMonth': 'Audits this month',
    'account.analytics.monthlyUsage': 'Monthly Usage',
    'account.analytics.monthlyUsageDesc': 'Audits and pages per month (last 6 months)',
    'account.analytics.pages': 'Pages',
    'account.analytics.projectsByStatus': 'Projects by Status',
    'account.analytics.topIndustries': 'Top Industries',
    'account.analytics.noData': 'No data available',
    'account.analytics.pending': 'Pending',
    'account.analytics.processing': 'Processing',
    'account.analytics.completed': 'Completed',
    'account.analytics.error': 'Error',
    'account.analytics.other': 'Other',
    
    // White Label
    'account.whitelabel.title': 'White Label Reports',
    'account.whitelabel.subtitle': 'Customize colors and branding of PDF reports',
    'account.whitelabel.primaryColor': 'Primary Color',
    'account.whitelabel.secondaryColor': 'Secondary Color',
    'account.whitelabel.preview': 'Preview',
    'account.whitelabel.save': 'Save White Label Settings',
    
    // Account Settings Grid
    'account.grid.plans': 'Plans & Usage',
    'account.grid.plansDesc': 'Manage subscription and packages',
    'account.grid.profile': 'Profile',
    'account.grid.profileDesc': 'Account and company details',
    'account.grid.members': 'Members',
    'account.grid.membersDesc': 'Manage account users',
    'account.grid.security': 'Security',
    'account.grid.securityDesc': 'Passwords and 2FA settings',
    'account.grid.notifications': 'Notifications',
    'account.grid.notificationsDesc': 'Email and alert preferences',
    'account.grid.invoice': 'Billing',
    'account.grid.invoiceDesc': 'Billing details and VAT',
    'account.grid.referral': 'Referrals',
    'account.grid.referralDesc': 'Referral program and rewards',
    'account.grid.analytics': 'Analytics',
    'account.grid.analyticsDesc': 'Monthly usage statistics',
    'account.grid.export': 'Export Data',
    'account.grid.exportDesc': 'Data export (GDPR)',
    'account.grid.activity': 'Activity',
    'account.grid.activityDesc': 'Action history',
    'account.grid.integrations': 'Integrations',
    'account.grid.integrationsDesc': 'Connect to Google Analytics',
    'account.grid.whitelabel': 'White Label',
    'account.grid.whitelabelDesc': 'Branding and colors',
    
    // Payment Status
    'account.payment.completed': 'Completed',
    'account.payment.pending': 'Pending',
    'account.payment.failed': 'Failed',
    
    // Draft & Client Selector
    'draftFound': 'Saved Draft Found',
    'lastSaved': 'Last saved',
    'discard': 'Discard',
    'restore': 'Restore',
    'loadingClients': 'Loading clients...',
    'linkToClient': 'Share Link',
    'selected': 'Selected',
    'selectClientOptional': 'Select client (optional)',
    'noClient': 'No client',
    'createNewClient': 'Create new client',
    'existingClients': 'Existing Clients',
    'newClient': 'New Client',
    'newClientDesc': 'Create a new client. Other details will be auto-filled from the analysis.',
    'clientNameBrand': 'Client Name / Brand',
    'clientNamePlaceholder': 'e.g. MyShop, TechCorp',
    'enterClientName': 'Enter client name',
    'clientCreated': 'Client created',
    'clientCreateError': 'Error creating client',
    'cancel': 'Cancel',
    'create': 'Create',
    'creating': 'Creating...',
    'linkedWith': 'Linked with',
    'disconnectedFromClient': 'Disconnected from client',
    'updateError': 'Update error',
    
    // Draft Indicator
    'draftSaving': 'Saving...',
    'draftSaved': 'Saved',
    'draftSavedShort': 'Saved',
    'draftNotSaved': 'Not saved',
    'draftRestore': 'Restore',
    'draftDelete': 'Delete Draft',
    'draftDeleteConfirmTitle': 'Delete Draft?',
    'draftDeleteConfirmDescription': 'This action will delete all saved wizard data. This cannot be undone.',
    'delete': 'Delete',
    
    // Referral Program
    'referral.title': 'Referral Program',
    'referral.subtitle': 'Earn free audits by referring friends',
    'referral.totalReferrals': 'Total Referrals',
    'referral.successful': 'Successful',
    'referral.activeRewards': 'Active Rewards',
    'referral.yourUniqueLink': 'Your Unique Link',
    'referral.shareAndEarn': 'Share and earn',
    'referral.copyLink': 'Copy link',
    'referral.discountCode': 'Discount Code',
    'referral.discountForFriends': '10% discount for your friends',
    'referral.copyCode': 'Copy code',
    'referral.friendsReceive': 'Your friends receive',
    'referral.discount': 'discount',
    'referral.onFirstPurchase': 'on their first purchase using this code.',
    'referral.howItWorks': 'How It Works',
    'referral.share': 'Share',
    'referral.shareDescription': 'Send your unique link to friends or colleagues',
    'referral.signup': 'Complete Signup',
    'referral.signupDescription': 'Friend verifies email, completes profile & runs audit → You +10 credits',
    'referral.purchase': 'Purchase',
    'referral.purchaseDescription': 'Friend buys a package with 40% off → You +100 credits',
    'referral.earn': 'Earn',
    'referral.earnDescription': '10 credits for signup + 100 credits for each purchase!',
    'referral.samePlan': 'credits',
    'referral.for30Days': 'permanently!',
    'referral.bonusPagesInfo': 'Credits per Referral',
    'referral.bonusPagesInfoText': 'Earn 10 credits when friend completes signup (verified + onboarding + 1st audit) and 100 credits when they buy any package!',
    'referral.rewardExample': 'Reward Summary',
    'referral.rewardExampleText': 'Successful signup = 10 credits | Any purchase = 100 credits | Your friend = 40% discount',
    'referral.activeRewardsTitle': 'Credits Earned',
    'referral.activeRewardsDesc': 'Audits you have earned from referrals',
    'referral.pagesPerAudit': 'pages per audit',
    'referral.expires': 'Expires',
    'referral.earned': 'Earned',
    'referral.history': 'Referral History',
    'referral.inProgress': 'in progress',
    'referral.noReferralsYet': 'No referrals yet',
    'referral.noReferralsDesc': 'Share your link and start earning free audits!',
    'referral.referral': 'Referral',
    'referral.completed': 'Completed',
    'referral.statusPending': 'Pending',
    'referral.statusRegistered': 'Registered',
    'referral.statusConverted': 'Completed',
    'referral.statusExpired': 'Expired',
    'referral.linkCopied': 'Link copied!',
    'referral.codeCopied': 'Discount code copied!',
    
    // Usage Analytics
    'analytics.totalProjects': 'Total Projects',
    'analytics.pagesAnalyzed': 'Pages Analyzed',
    'analytics.totalAudits': 'Total Audits',
    'analytics.auditsThisMonth': 'Audits this month',
    'analytics.monthlyUsage': 'Monthly Usage',
    'analytics.monthlyUsageDesc': 'Audits and pages per month (last 6 months)',
    'analytics.pages': 'Pages',
    'analytics.projectsByStatus': 'Projects by Status',
    'analytics.topIndustries': 'Top Industries',
    'analytics.noData': 'No data available',
    'analytics.other': 'Other',
    'analytics.statusPending': 'Pending',
    'analytics.statusProcessing': 'Processing',
    'analytics.statusCompleted': 'Completed',
    'analytics.statusError': 'Error',
    
    // Settings toasts
    'settings.profileLoadError': 'Error loading profile',
    'settings.logoUploaded': 'Logo uploaded successfully',
    'settings.logoUploadError': 'Error uploading logo',
    'settings.profileSaved': 'Profile saved successfully',
    'settings.profileSaveError': 'Error saving profile',
    'settings.billingSaved': 'Billing details saved',
    'settings.billingSaveError': 'Error saving billing details',
  },
  // The site is English-only (see LanguageProvider). The Greek dictionary was
  // never read and weighed ~75 KB in the first JavaScript file every visitor loads.
  el: {},
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  // Public site is English-only. Any legacy 'el' preference stored in a
  // visitor's localStorage from the previous bilingual setup is ignored and
  // wiped, so pages that branch on `language === 'el'` always render English.
  const [language, setLanguageState] = useState<Language>('en');

  useEffect(() => {
    try {
      localStorage.removeItem('eyecaptain-language');
    } catch {
      /* no-op */
    }
    document.documentElement.lang = 'en';
  }, []);

  const setLanguage = (_lang: Language) => {
    // No-op: site is English-only. Kept for backwards compatibility with
    // legacy callers (e.g. LanguageSwitcher) that may still exist.
    setLanguageState('en');
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  const getLocalizedPath = (path: string): string => {
    // Remove existing language prefix if any
    const cleanPath = path.replace(/^\/(el|en)/, '');
    return getLocalizedPathForLang(cleanPath || '/', language);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, getLocalizedPath }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
